/**
 * CUA Error Messages — Vietnamese human-readable messages for all 11 CUA error codes
 * Reference: TASK-UI-CU-012-1, SOL-UI-CU-012
 */

export type CUAErrorCode =
  | 'blocked_app'
  | 'credential_detected'
  | 'cua_rate_limit'
  | 'cua_quota_exceeded'
  | 'connector_not_granted'
  | 'loop_detected'
  | 'budget_exceeded'
  | 'budget_write_exceeded'
  | 'budget_screenshots_exceeded'
  | 'space_not_found'
  | 'space_creating'
  | 'driver_not_connected';

/**
 * Map a CUA error code and optional details to a Vietnamese user-facing message.
 * All 11 codes are covered; unknown codes return a generic fallback.
 */
export function mapCUAError(code: string, details: Record<string, unknown> = {}): string {
  switch (code as CUAErrorCode) {
    case 'blocked_app': {
      const app = String(details?.app ?? 'ứng dụng này');
      return `Ứng dụng "${app}" bị chặn theo chính sách bảo mật. Agent không thể tương tác với ứng dụng này.`;
    }
    case 'credential_detected':
      return 'Agent cố nhập thông tin nhạy cảm (mật khẩu, API key) — đã chặn tự động.';
    case 'cua_rate_limit': {
      const retryAfter = Number(details?.retry_after_sec ?? 60);
      return `Quá nhiều thao tác CUA trong thời gian ngắn. Thử lại sau ${retryAfter}s.`;
    }
    case 'cua_quota_exceeded': {
      const metric = String(details?.metric ?? 'sessions');
      const limit = Number(details?.limit ?? 0);
      return `Đã vượt giới hạn CUA cho "${metric}" (giới hạn: ${limit}). Vui lòng nâng cấp gói hoặc chờ reset.`;
    }
    case 'connector_not_granted':
      return 'Agent yêu cầu quyền truy cập màn hình (Computer-Use). Vui lòng xác nhận consent.';
    case 'loop_detected':
      return 'Phát hiện Agent lặp thao tác quá nhiều lần. Phiên đã bị tạm dừng tự động.';
    case 'budget_exceeded': {
      const used = Number(details?.used ?? 0);
      const limit = Number(details?.limit ?? 0);
      return `Ngân sách thao tác đã hết (${used}/${limit}). Agent không thể tiếp tục trong phiên này.`;
    }
    case 'budget_write_exceeded': {
      const used = Number(details?.used ?? 0);
      const limit = Number(details?.limit ?? 0);
      return `Giới hạn thao tác ghi đã đạt (${used}/${limit}). Agent chỉ có thể đọc trong phiên này.`;
    }
    case 'budget_screenshots_exceeded': {
      const used = Number(details?.used ?? 0);
      const limit = Number(details?.limit ?? 0);
      return `Số lượng chụp màn hình đã hết (${used}/${limit}). Agent không thể chụp thêm.`;
    }
    case 'space_not_found':
      return 'Cua Space không tìm thấy hoặc đã bị xóa. Vui lòng tạo Space mới.';
    case 'space_creating':
      return 'Cua Space đang khởi tạo. Vui lòng chờ vài giây rồi thử lại.';
    case 'driver_not_connected':
      return 'CUA Driver chưa kết nối. Hãy kiểm tra vnp-cua-driver đã được cài đặt và đang chạy.';
    default:
      return `Đã xảy ra lỗi CUA: ${code}. Vui lòng thử lại.`;
  }
}
